import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useTaxStatements from 'features/myVaro/hooks/statementsAndForms/useTaxStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const TaxYearlyStatementSelectionScreen = () => {
  const router = useRouter();
  const [documentId, setDocumentId] = useState('');
  const flow = useVaroUserFlow();
  const {
    setTaxStatementPDFId,
    setTaxYearSelectionScreenTitle,
    taxYearSelectionScreenTitle,
    setTaxStatementPDFTitle,
  } = useContext(MyVaroContext);
  const { year } = useLocalSearchParams<{ year: string }>();

  const {
    yearlyStatementsData,
    taxStatementsDetails,
    documentDetails,
    errorModel,
    loading,
  } = useTaxStatements({
    year: year,
    documentId: documentId,
  });

  //Handles Screen title if we hit the url directly or get redirected from another screen
  useEffect(() => {
    taxStatementsDetails?.forEach((tax) => {
      if (tax?.year?.toString() === year) {
        setTaxYearSelectionScreenTitle?.(tax.title);
      }
    });
  }, [setTaxYearSelectionScreenTitle, taxStatementsDetails, year]);

  useEffect(() => {
    if (documentId !== '' && documentDetails?.document?.url) {
      setDocumentId('');
      setTaxStatementPDFId?.(documentDetails.document.url);
      setTaxStatementPDFTitle?.(documentDetails.document.title);

      router.push({
        pathname: `/${flow}/statements-and-forms/tax-statements/[year]/statement`,
        params: {
          year: year,
        },
      });
    }
  }, [
    documentId,
    setTaxStatementPDFId,
    year,
    documentDetails,
    setTaxStatementPDFTitle,
    router,
    flow,
  ]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: taxYearSelectionScreenTitle,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <FlatList
        data={yearlyStatementsData}
        renderItem={({ item }) => (
          <ListItem
            analyticsId={item?.id}
            key={item?.id}
            primaryCopy={item?.title}
            onPress={() => {
              setDocumentId(item?.documentId);
            }}
          />
        )}
        ItemSeparatorComponent={() => (
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
        )}
        keyExtractor={(item) => item?.id}
        removeClippedSubviews={false}
      />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { backgroundColor: theme.color.background.primary, flexGrow: 1 },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default TaxYearlyStatementSelectionScreen;
